import moment from "moment";

import { PracticeCropWithDefaults, generateRandomId, getEnvironment, isFieldEnrolled, isImpersonating } from "utils";

import {
  AllCropsPracticeDataByYear,
  AllCropsPracticeDataItem,
  CROP_TYPE_FCPO,
  CarbonProgram,
  CarbonProgramResult,
  CropFCPO,
  FertilizerManagement,
  FieldListField,
  FieldPracticeResponse,
  PRACTICE_SLUG,
  PracticeBundle,
  PracticeFCPO,
  PracticeSlug,
  VerificationEditingRequest,
  ZERO_UUID,
} from "models";

import { type BulkEditCrop } from "pages/app/all-field-view/components/BulkEditCropDrawer.tsx";
import { DATA_STATUS } from "pages/app/all-field-view/components/FieldFilterOptions";
import {
  type MandatoryYearsByPracticeSlug,
  getCropMissingDataRules,
  getCropYearFromDates,
  getFieldMandatoryYears,
  getMandatoryYearsByPracticeSlug,
  hasOpenCropEditingRequest,
  hasOpenPracticeEditingRequest,
} from "pages/app/all-field-view/utils/AllFieldsUtils";
import { getCropDateEditDisabled, getIsYieldEditDisabled, getPracticeInputsDisabled } from "pages/app/all-field-view/utils/dataEntryUtils.ts";
import { getInputRequirementConfig, isRequired } from "pages/app/all-field-view/utils/getInputRequirementConfig";
import { type AllFieldsDataTableFiltersState } from "pages/app/all-fields-data-table-view/components/AllFieldsDataTableFilters";
import { getBaselineYearsByPracticeType } from "pages/app/field-timeline/utils/PracticeUtils.ts";
import { type MissingDataForm } from "pages/app/historical-practice-data-table-view/components/AddEditMissingDataDrawer.tsx";
import { getEditableState } from "pages/app/historical-practice-data-table-view/utils/HistoricalDataUtils.ts";

// which columns are applicable to a crop's type at all (e.g. UNDERSOWING crops only ever have a growing period —
// everything else renders "NA"), derived from getInputRequirementConfig(..., ignoreCarbonProgramConditions: true)
export type AllFieldsDataTableRowApplicability = {
  growingPeriod: boolean;
  seedings: boolean;
  tillages: boolean;
  organicFertilizers: boolean;
  // main and cover crops only, and only when the carbon program actually offers mineral fertilizer — unlike the
  // other practices, this one isn't shown unconditionally (e.g. inventory-methodology programs that don't define
  // or enroll it shouldn't show the column at all)
  mineralFertilizers: boolean;
  yield: boolean;
};

// mirrors Timeline's own "can a crop actually be added for this year" window (PlaceholderUtils.ts's
// getFieldBaselineYears fallback + its 5-year-forward limit) — used to gate both the Harvest Year filter and
// the "+ Add crop" actions in Table view, so the two stay in sync with a single source of truth
const ADD_CROP_MAX_FUTURE_YEARS = 5;

export const getAddCropYearWindow = (
  activeFarmReportingYear: number,
  monitoringPeriodType: CarbonProgram["fieldsManagement"]["monitoringPeriodType"] | undefined
) => {
  const baselineYearsCount = monitoringPeriodType === "harvest" ? 4 : 3;
  return { startYear: activeFarmReportingYear - baselineYearsCount, endYear: activeFarmReportingYear + ADD_CROP_MAX_FUTURE_YEARS };
};

// Table-view-only display fix: a side crop that straddles a year boundary is moved into its termination year, leaving Timeline's own bucketing untouched
export const rebucketSideCropsByRelatedMainCropYear = (allCropsPracticeDataByYear: AllCropsPracticeDataByYear): AllCropsPracticeDataByYear => {
  const rebucketed: AllCropsPracticeDataByYear = {};

  Object.entries(allCropsPracticeDataByYear).forEach(([yearKey, entries]) => {
    const originalYear = Number(yearKey);

    entries.forEach((entry) => {
      if (entry.crop.cropType === CROP_TYPE_FCPO.MAIN) {
        (rebucketed[originalYear] ??= []).push(entry);
        return;
      }

      const terminationYear = moment(entry.crop.harvestEndDate ?? entry.crop.fallbackDates.harvestEndDate).year();
      (rebucketed[terminationYear] ??= []).push(entry);
    });
  });

  return rebucketed;
};

// Table-view-only display fix: a Main crop whose backend year differs from the calendar year of its harvest (e.g. wrapping reporting periods) displays under the calendar year instead
export const rebucketMainCropsByCalendarHarvestYear = (allCropsPracticeDataByYear: AllCropsPracticeDataByYear): AllCropsPracticeDataByYear => {
  const rebucketed: AllCropsPracticeDataByYear = {};

  Object.entries(allCropsPracticeDataByYear).forEach(([yearKey, entries]) => {
    const originalYear = Number(yearKey);

    entries.forEach((entry) => {
      if (entry.crop.cropType !== CROP_TYPE_FCPO.MAIN) {
        (rebucketed[originalYear] ??= []).push(entry);
        return;
      }

      const calendarHarvestYear = moment(entry.crop.harvestEndDate ?? entry.crop.fallbackDates.harvestEndDate).year();
      (rebucketed[calendarHarvestYear] ??= []).push(entry);
    });
  });

  return rebucketed;
};

export const isMineralFertilizerOfferedByProgram = (carbonProgram: CarbonProgram | null): boolean => {
  const mineralFertilizerPractice = carbonProgram?.fieldsManagement.practices?.find((practice) => practice.slug === PRACTICE_SLUG.MINERAL_FERTILIZERS);
  return !!mineralFertilizerPractice && (mineralFertilizerPractice.required || mineralFertilizerPractice.enrollable);
};

export const getAllFieldsDataTableRowApplicability = (
  crop: CropFCPO,
  carbonProgram: CarbonProgram | null,
  hasExistingMineralFertilizerData = false,
  hasExistingYieldValue = false
): AllFieldsDataTableRowApplicability => {
  const inputRequirementConfig = getInputRequirementConfig(carbonProgram, [], crop, true, true);

  return {
    growingPeriod: true,
    seedings: inputRequirementConfig?.seedings !== undefined,
    tillages: inputRequirementConfig?.tillages !== undefined,
    organicFertilizers: inputRequirementConfig?.organicFertilizers !== undefined,
    mineralFertilizers:
      inputRequirementConfig?.mineralFertilizers !== undefined && (isMineralFertilizerOfferedByProgram(carbonProgram) || hasExistingMineralFertilizerData),
    yield: inputRequirementConfig?.yield !== undefined || hasExistingYieldValue,
  };
};

// whether a field must have the given practice slug for the selected year, per the program's mandatory-years rule
export const isPracticeMandatoryForYear = (
  practiceSlug: PracticeSlug,
  availablePracticeBundles: PracticeBundle[],
  carbonProgram: CarbonProgram | null | undefined,
  activeFarmReportingYear: number | undefined,
  selectedYear: number | undefined
): boolean => {
  if (!carbonProgram || activeFarmReportingYear === undefined || selectedYear === undefined) return false;
  return (getMandatoryYearsByPracticeSlug(availablePracticeBundles, carbonProgram, activeFarmReportingYear)[practiceSlug] ?? []).includes(selectedYear);
};

// per-column completeness for a row, treating a column that isn't applicable to this crop's type as complete
// (it renders "NA" and never counts against the row's overall status)
export type AllFieldsDataTableRowCompleteness = {
  growingPeriod: boolean;
  seedings: boolean;
  tillages: boolean;
  organicFertilizers: boolean;
  // optional practice — tracked for the "missing" filter but deliberately excluded from isComplete/the status column
  mineralFertilizers: boolean;
  yield: boolean;
  isComplete: boolean;
};

// practices a row must have filled in — like Timeline, only the ones mandatory for the crop's year
export type AllFieldsDataTableRequiredPractices = { seedings: boolean; tillages: boolean; organicFertilizers: boolean; yield: boolean };

const ALL_PRACTICES_REQUIRED: AllFieldsDataTableRequiredPractices = { seedings: true, tillages: true, organicFertilizers: true, yield: true };

const hasMissingEndDate = (practices: AllCropsPracticeDataItem["assignedPractices"]["tillages"]) =>
  practices !== null && practices.some((practice) => practice.endDate === null && practice.assignedPracticeId !== ZERO_UUID);

export const getAllFieldsDataTableRowCompleteness = (
  entry: AllCropsPracticeDataItem,
  carbonProgram: CarbonProgram | null,
  cropDetails: PracticeCropWithDefaults | undefined,
  requiredPractices: AllFieldsDataTableRequiredPractices = ALL_PRACTICES_REQUIRED
): AllFieldsDataTableRowCompleteness => {
  const { crop, assignedPractices, isTillageDepthRequired } = entry;
  const applicability = getAllFieldsDataTableRowApplicability(
    crop,
    carbonProgram,
    (assignedPractices.mineralFertilizers?.length ?? 0) > 0,
    crop.yieldPerHa != null
  );

  const growingPeriod = crop.seedingStartDate !== null && crop.harvestEndDate !== null;

  const seedingsComplete = !requiredPractices.seedings || !applicability.seedings || (assignedPractices.seedings?.length ?? 0) > 0;

  const { tillages } = assignedPractices;
  const hasMissingTillageDepth =
    isTillageDepthRequired && tillages !== null && tillages.some((practice) => practice.tillageDepth === null && practice.assignedPracticeId !== ZERO_UUID);
  const tillagesComplete =
    !requiredPractices.tillages || !applicability.tillages || ((tillages?.length ?? 0) > 0 && !hasMissingTillageDepth && !hasMissingEndDate(tillages));

  const organicFertilizersComplete =
    !requiredPractices.organicFertilizers ||
    !applicability.organicFertilizers ||
    ((assignedPractices.organicFertilizers?.length ?? 0) > 0 && !hasMissingEndDate(assignedPractices.organicFertilizers));

  // optional practice — tracked here for the "missing" filter, deliberately left out of isComplete below
  const mineralFertilizersComplete =
    !applicability.mineralFertilizers || ((assignedPractices.mineralFertilizers?.length ?? 0) > 0 && !hasMissingEndDate(assignedPractices.mineralFertilizers));

  const isZeroYieldAllowed = cropDetails?.harvestIsMandatory === false;
  const hasYieldValue = typeof crop.yieldPerHa === "number" && (crop.yieldPerHa > 0 || (crop.yieldPerHa === 0 && isZeroYieldAllowed));
  // like Timeline, a yield is only missing when the program requires it and the crop's year is mandatory for crops
  const isYieldRequired = requiredPractices.yield && isRequired(getInputRequirementConfig(carbonProgram, [], crop, true)?.yield);
  const yieldComplete = !isYieldRequired || crop.wasHarvested === false || hasYieldValue;

  const isComplete = growingPeriod && seedingsComplete && tillagesComplete && organicFertilizersComplete && yieldComplete;

  return {
    growingPeriod,
    seedings: seedingsComplete,
    tillages: tillagesComplete,
    organicFertilizers: organicFertilizersComplete,
    mineralFertilizers: mineralFertilizersComplete,
    yield: yieldComplete,
    isComplete,
  };
};

// looks up a crop's PracticeCropWithDefaults (e.g. harvestIsMandatory), shared by row-completeness call sites
const getCropDetailsForEntry = (entry: AllCropsPracticeDataItem, practiceCrops: PracticeCropWithDefaults[] | undefined) =>
  practiceCrops?.find((practiceCrop) => practiceCrop.id === entry.crop.cropId);

// shared by getYearsWithMissingData/getFieldIdsWithMissingDataForYear: missing if no main crop that year, or an existing row is incomplete
const isFieldMissingDataForYear = (
  field: FieldListField,
  year: number,
  allCropsPracticeDataByYear: AllCropsPracticeDataByYear,
  carbonProgram: CarbonProgram,
  practiceCrops: PracticeCropWithDefaults[] | undefined,
  mandatoryYearsByPracticeSlug: MandatoryYearsByPracticeSlug
): boolean => {
  const fieldEntriesForYear = (allCropsPracticeDataByYear[year] ?? []).filter((entry) => entry.fieldId === field.id);
  const hasMainCropForYear = fieldEntriesForYear.some((entry) => entry.crop.cropType === CROP_TYPE_FCPO.MAIN);
  if (!hasMainCropForYear) return true;

  return fieldEntriesForYear.some((entry) => {
    // checked against the crop's own reference year like the row's status, since a Cover/Undersowing crop can be rebucketed
    // into a later display year; a crop from before the baseline (e.g. sown the autumn before) doesn't count as missing
    const cropYear = getCropYearFromDates(entry.crop.cropType, moment(entry.crop.seedingStartDate), moment(entry.crop.harvestEndDate)) ?? year;
    const { requiredPractices, isMineralFertilizerRequired, isBeforeMandatoryYears } = getCropMissingDataRules(mandatoryYearsByPracticeSlug, cropYear);
    if (isBeforeMandatoryYears) return false;

    const completeness = getAllFieldsDataTableRowCompleteness(entry, carbonProgram, getCropDetailsForEntry(entry, practiceCrops), requiredPractices);
    return !completeness.isComplete || (isMineralFertilizerRequired && !completeness.mineralFertilizers);
  });
};

// years to flag with a "missing data" indicator (Harvest Year dropdown dot) — mirrors Timeline's own open-tasks logic
export const getYearsWithMissingData = (
  years: number[],
  activeFarmReportingYear: number,
  fields: FieldListField[] | undefined,
  allCropsPracticeDataByYear: AllCropsPracticeDataByYear,
  allFieldsPractices: Record<string, FieldPracticeResponse> | undefined,
  carbonProgram: CarbonProgram | null,
  practiceCrops: PracticeCropWithDefaults[] | undefined
): Set<number> => {
  if (!carbonProgram) return new Set();

  const availableYears = new Set(years);
  const yearsWithMissingData = new Set<number>();

  (fields ?? []).forEach((field) => {
    const availablePracticeBundles = allFieldsPractices?.[field.id]?.availablePracticeBundles ?? [];
    if (!isFieldEnrolled(availablePracticeBundles, carbonProgram)) return;

    const mandatoryYearsByPracticeSlug = getMandatoryYearsByPracticeSlug(availablePracticeBundles, carbonProgram, activeFarmReportingYear);
    const mandatoryYears = Array.from(new Set(Object.values(mandatoryYearsByPracticeSlug).flat())).filter((year) => availableYears.has(year));
    mandatoryYears.forEach((year) => {
      if (yearsWithMissingData.has(year)) return;
      if (isFieldMissingDataForYear(field, year, allCropsPracticeDataByYear, carbonProgram, practiceCrops, mandatoryYearsByPracticeSlug)) {
        yearsWithMissingData.add(year);
      }
    });
  });

  return yearsWithMissingData;
};

// field-level counterpart to getYearsWithMissingData, scoped to a single year — Table-view-local stand-in for Timeline's farm-wide fieldsWithOpenTasksCount
export const getFieldIdsWithMissingDataForYear = (
  year: number,
  fields: FieldListField[] | undefined,
  activeFarmReportingYear: number,
  allCropsPracticeDataByYear: AllCropsPracticeDataByYear,
  allFieldsPractices: Record<string, FieldPracticeResponse> | undefined,
  carbonProgram: CarbonProgram | null,
  practiceCrops: PracticeCropWithDefaults[] | undefined
): Set<string> => {
  if (!carbonProgram) return new Set();

  const fieldIdsWithMissingData = new Set<string>();

  (fields ?? []).forEach((field) => {
    const availablePracticeBundles = allFieldsPractices?.[field.id]?.availablePracticeBundles ?? [];
    if (!isFieldEnrolled(availablePracticeBundles, carbonProgram)) return;

    const mandatoryYearsByPracticeSlug = getMandatoryYearsByPracticeSlug(availablePracticeBundles, carbonProgram, activeFarmReportingYear);

    if (isFieldMissingDataForYear(field, year, allCropsPracticeDataByYear, carbonProgram, practiceCrops, mandatoryYearsByPracticeSlug)) {
      fieldIdsWithMissingData.add(field.id);
    }
  });

  return fieldIdsWithMissingData;
};

// applies the crop/previous-crop/growing-period/data-status/missing-type filters to a year's entries, then
// re-sorts to match the field order the table itself uses (the page passes fields pre-sorted per FieldSortOptions)
export const filterAndSortEntries = (
  entries: AllCropsPracticeDataItem[],
  {
    fieldOrderIndex,
    filters,
    dataStatusFilter,
    carbonProgram,
    practiceCrops,
    allFieldsPractices,
    activeFarmReportingYear,
    selectedYear,
    verificationEditingRequests = [],
  }: {
    fieldOrderIndex: Map<string, number>;
    filters: AllFieldsDataTableFiltersState;
    dataStatusFilter: DATA_STATUS;
    carbonProgram: CarbonProgram | null;
    practiceCrops: PracticeCropWithDefaults[] | undefined;
    allFieldsPractices: Record<string, FieldPracticeResponse> | undefined;
    activeFarmReportingYear: number | undefined;
    selectedYear: number;
    verificationEditingRequests?: VerificationEditingRequest[];
  }
): AllCropsPracticeDataItem[] => {
  const cropIdSet = new Set(filters.appliedCropIds);
  const previousCropIdSet = new Set(filters.previouslyAppliedCropIds);
  const hasMissingTypeFilter =
    filters.missingSeedings || filters.missingTillages || filters.missingOrganicFertilizers || filters.missingMineralFertilizers || filters.missingYield;
  // a cover/undersowing crop can be fully complete on its own and still leave the field incomplete overall while
  // no Main crop has been added for the year — matches the same check behind the status column's badge count
  const fieldIdsWithMainCropThisYear = new Set(entries.filter((entry) => entry.crop.cropType === CROP_TYPE_FCPO.MAIN).map((entry) => entry.fieldId));

  const matchingEntries = entries.filter((entry) => {
    if (!fieldOrderIndex.has(entry.fieldId)) return false;
    if (cropIdSet.size > 0 && !cropIdSet.has(entry.crop.cropId)) return false;
    if (previousCropIdSet.size > 0 && (!entry.previousMainCrop || !previousCropIdSet.has(entry.previousMainCrop.cropId))) return false;

    // checked against the crop's own reference year (matching Timeline), since a Cover/Undersowing crop can be rebucketed to a different display year
    const cropYear = getCropYearFromDates(entry.crop.cropType, moment(entry.crop.seedingStartDate), moment(entry.crop.harvestEndDate)) ?? selectedYear;
    const { requiredPractices, isMineralFertilizerRequired, isBeforeMandatoryYears } = getCropMissingDataRules(
      getFieldMandatoryYears(allFieldsPractices?.[entry.fieldId]?.availablePracticeBundles ?? [], carbonProgram, activeFarmReportingYear),
      cropYear
    );
    const completeness = getAllFieldsDataTableRowCompleteness(entry, carbonProgram, getCropDetailsForEntry(entry, practiceCrops), requiredPractices);

    if (filters.missingGrowingPeriod && completeness.growingPeriod) return false;

    if (dataStatusFilter !== DATA_STATUS.ALL) {
      // non-enrolled fields match neither status bucket, matching Timeline's behavior
      const isEnrolled = isFieldEnrolled(allFieldsPractices?.[entry.fieldId]?.availablePracticeBundles ?? [], carbonProgram);
      if (!isEnrolled) return false;

      // a crop with an open editing request is never "completed", even with no missing data — it still needs
      // attention — so it belongs in the incomplete bucket, matching the status column's badge/dot logic
      const hasOpenEditingRequest =
        hasOpenCropEditingRequest(verificationEditingRequests, entry.crop.id) ||
        hasOpenPracticeEditingRequest(verificationEditingRequests, {
          seedingPracticeId: entry.crop.seedingPracticeId,
          practiceIds: [
            ...(entry.assignedPractices[PRACTICE_SLUG.TILLAGES] ?? []),
            ...(entry.assignedPractices[PRACTICE_SLUG.ORGANIC_FERTILIZERS] ?? []),
            ...(entry.assignedPractices[PRACTICE_SLUG.MINERAL_FERTILIZERS] ?? []),
          ]
            .map((practice) => practice.id)
            .filter((id): id is string => !!id),
        });

      const isMissingMainCropForField = entry.crop.cropType !== CROP_TYPE_FCPO.MAIN && !fieldIdsWithMainCropThisYear.has(entry.fieldId);

      // mineral fertilizer is excluded from completeness.isComplete, but counts as missing once mandatory — matches the status column's badge count
      const isRowComplete =
        isBeforeMandatoryYears || (completeness.isComplete && !isMissingMainCropForField && (!isMineralFertilizerRequired || completeness.mineralFertilizers));

      const matchesStatus = dataStatusFilter === DATA_STATUS.COMPLETED ? isRowComplete && !hasOpenEditingRequest : !isRowComplete || hasOpenEditingRequest;
      if (!matchesStatus) return false;
    }

    if (!hasMissingTypeFilter) return true;

    const missingSeedings = filters.missingSeedings && !completeness.seedings;
    const missingTillages = filters.missingTillages && !completeness.tillages;
    const missingOrganicFertilizers = filters.missingOrganicFertilizers && !completeness.organicFertilizers;
    const missingMineralFertilizers = filters.missingMineralFertilizers && !completeness.mineralFertilizers;
    const missingYield = filters.missingYield && !completeness.yield;

    // OR semantics: show a row if it matches any selected missing-type filter
    return missingSeedings || missingTillages || missingOrganicFertilizers || missingMineralFertilizers || missingYield;
  });

  // within a field, the Table view's sub-row grouping expects Main first, then Cover/Undersowing — enum
  // declaration order (MAIN, COVER, UNDERSOWING) already matches the desired display order
  const cropTypeOrder = Object.values(CROP_TYPE_FCPO);

  return matchingEntries.sort((entryA, entryB) => {
    const fieldOrderDiff = (fieldOrderIndex.get(entryA.fieldId) ?? 0) - (fieldOrderIndex.get(entryB.fieldId) ?? 0);
    if (fieldOrderDiff !== 0) return fieldOrderDiff;
    return cropTypeOrder.indexOf(entryA.crop.cropType) - cropTypeOrder.indexOf(entryB.crop.cropType);
  });
};

// builds the MissingDataForm used by both the missing-data drawer and the multi-select bar for a given entry
export const buildMissingDataForm = (
  entry: AllCropsPracticeDataItem,
  {
    activeFarmReportingYear,
    isReportingYearLocked,
    selectedYear,
    carbonProgram,
    availablePracticeBundles,
    carbonProgramOutcomesForField,
    isDataSubmitted,
    lastSubmissionDate,
    lastSubmissionYear,
    verificationEditingRequests = [],
  }: {
    activeFarmReportingYear: number | undefined;
    isReportingYearLocked: boolean;
    selectedYear: number | undefined;
    carbonProgram: CarbonProgram | null;
    availablePracticeBundles: PracticeBundle[];
    carbonProgramOutcomesForField: CarbonProgramResult | undefined;
    isDataSubmitted: boolean;
    lastSubmissionDate: string | null | undefined;
    lastSubmissionYear: number | null | undefined;
    verificationEditingRequests?: VerificationEditingRequest[];
  }
): MissingDataForm => {
  const { crop, assignedPractices } = entry;
  const isAdminOrImpersonator = getEnvironment().admin || isImpersonating();
  const {
    isTillageEditable,
    isTillageDepthEditable,
    areTillageDatesEditable,
    isOrganicEditable,
    areOrganicDatesEditable,
    isMineralEditable,
    areMineralDatesEditable,
  } = getEditableState(
    entry,
    activeFarmReportingYear,
    isReportingYearLocked,
    selectedYear ?? crop.year ?? undefined,
    isAdminOrImpersonator,
    lastSubmissionYear
  );

  const fieldIsEnrolled = isFieldEnrolled(availablePracticeBundles, carbonProgram);
  const bundleHasConfirmedData = (bundleSlug: string) => availablePracticeBundles.find((bundle) => bundle.slug === bundleSlug)?.hasConfirmedData;
  const baselineYearsByPracticeType = carbonProgramOutcomesForField ? getBaselineYearsByPracticeType(carbonProgramOutcomesForField) : {};
  const cropYear = Number(crop.year);

  const { isCropSeedingDateEditDisabled, isCropHarvestDateEditDisabled, isCropNameEditDisabled } = getCropDateEditDisabled({
    crop,
    carbonProgram,
    activeFarmReportingYear,
    lastSubmissionDate,
    lastSubmissionYear,
    isAdmin: isAdminOrImpersonator,
  });
  const isGrowingPeriodEditable = !(isCropSeedingDateEditDisabled && isCropHarvestDateEditDisabled);

  const requirementConfig = getInputRequirementConfig(carbonProgram, availablePracticeBundles, crop, fieldIsEnrolled);
  const disableSeedingInputs = getPracticeInputsDisabled({
    isPartOfSoilPlus3DataCollection: !!crop.seedingSoilPlus3DataCollection,
    baselineYears: baselineYearsByPracticeType?.seedings,
    cropYear,
    activeFarmReportingYear,
    isDataSubmitted,
    lastSubmissionYear,
    bundleHasConfirmedData: bundleHasConfirmedData(PRACTICE_SLUG.SEEDINGS),
    isAdmin: isAdminOrImpersonator,
  });
  // an empty-but-required seeding field stays editable even if the historic-year/bundle-confirmed check above would lock it
  const hasPendingSeedingEditingRequest = hasOpenPracticeEditingRequest(verificationEditingRequests, {
    seedingPracticeId: crop.seedingPracticeId,
    practiceIds: [],
  });
  const isSeedingEditable = (isRequired(requirementConfig?.seedings) && !crop.seedingPracticeType) || !disableSeedingInputs || hasPendingSeedingEditingRequest;

  const hasExistingMineralFertilizerData = (assignedPractices.mineralFertilizers?.length ?? 0) > 0;
  const hasExistingYieldValue = crop.yieldPerHa != null;
  const applicability = getAllFieldsDataTableRowApplicability(crop, carbonProgram, hasExistingMineralFertilizerData, hasExistingYieldValue);

  return {
    key: entry.crop.id,
    fieldId: entry.fieldId,
    year: selectedYear ?? crop.year ?? moment().year(),
    crop,
    seedings: assignedPractices[PRACTICE_SLUG.SEEDINGS],
    tillages: assignedPractices[PRACTICE_SLUG.TILLAGES],
    organicFertilizers: assignedPractices[PRACTICE_SLUG.ORGANIC_FERTILIZERS],
    mineralFertilizers: assignedPractices[PRACTICE_SLUG.MINERAL_FERTILIZERS],
    isGrowingPeriodEditable,
    isCropNameEditable: !isCropNameEditDisabled,
    isSeedingEditable,
    isOrganicEditable,
    isTillageEditable,
    isTillageDepthEditable,
    areTillageDatesEditable,
    areOrganicDatesEditable,
    isMineralEditable,
    areMineralDatesEditable,
    // same lock as Timeline's crop form, so submitted yields can't be changed from the table either
    isYieldEditable:
      applicability.yield &&
      !getIsYieldEditDisabled({
        crop,
        isYieldRequired: isRequired(requirementConfig?.yield),
        isCropSeedingDateEditDisabled,
        isCropHarvestDateEditDisabled,
        lastSubmissionYear,
      }),
    yieldPerHa: crop.yieldPerHa,
    yieldPerHaUnit: crop.yieldPerHaUnit,
  };
};

// shared by AllFieldsDataTable's isRowSelectable (ref-based, for shift-select/select-all) and isItemLockedOut
// (state-based, for per-row disabled state) — a placeholder is always a Main-crop slot, so a Main or Cover crop's
// data can seed one, but not Undersowing, which itself requires a Main crop to already exist on the field
export const isCropSelectionCompatible = (
  item: MissingDataForm,
  {
    lockedCropType,
    realCropSelectedCount,
    hasPlaceholderSelected,
  }: { lockedCropType: CROP_TYPE_FCPO | undefined; realCropSelectedCount: number; hasPlaceholderSelected: boolean }
): boolean => {
  if (item.isPlaceholder) {
    if (realCropSelectedCount > 1) return false;
    return !(realCropSelectedCount === 1 && lockedCropType === CROP_TYPE_FCPO.UNDERSOWING);
  }
  if (lockedCropType && item.crop?.cropType !== lockedCropType) return false;
  if (hasPlaceholderSelected && realCropSelectedCount >= 1) return false;
  return !(hasPlaceholderSelected && item.crop?.cropType === CROP_TYPE_FCPO.UNDERSOWING);
};

// an already-checked row takes priority over the table's first row for select-all's "which type wins"
export const getSelectAllReferenceItem = (
  selectableRowKeys: string[],
  getMissingItemById: (id: string) => MissingDataForm | null,
  selectedItems: Map<string, MissingDataForm>
): MissingDataForm | null => selectedItems.values().next().value ?? getMissingItemById(selectableRowKeys[0]) ?? null;

const isSameSelectAllType = (item: MissingDataForm, referenceItem: MissingDataForm): boolean =>
  referenceItem.isPlaceholder ? !!item.isPlaceholder : !item.isPlaceholder && item.crop?.cropType === referenceItem.crop?.cropType;

// select-all only ever selects one kind of row: every placeholder, or every real crop of one type
export const getSelectAllMatchingItems = (
  selectableRowKeys: string[],
  getMissingItemById: (id: string) => MissingDataForm | null,
  selectedItems: Map<string, MissingDataForm>
): Map<string, MissingDataForm> => {
  const referenceItem = getSelectAllReferenceItem(selectableRowKeys, getMissingItemById, selectedItems);
  const matchingItems = new Map<string, MissingDataForm>();
  if (!referenceItem) return matchingItems;

  selectableRowKeys.forEach((key) => {
    const item = getMissingItemById(key);
    if (item && isSameSelectAllType(item, referenceItem)) matchingItems.set(key, item);
  });

  return matchingItems;
};

// true only when the selection matches exactly what getSelectAllMatchingItems would produce
export const isSelectAllFullySelected = (
  selectableRowKeys: string[],
  getMissingItemById: (id: string) => MissingDataForm | null,
  selectedItems: Map<string, MissingDataForm>,
  selectedRowCount: number
): boolean => {
  if (selectedRowCount === 0) return false;
  const selected = [...selectedItems.values()];
  const isPlaceholderSelection = selected.every((item) => item.isPlaceholder);
  const selectedCropTypes = new Set(selected.filter((item) => !item.isPlaceholder).map((item) => item.crop?.cropType));
  const isSingleRealCropTypeSelection = !isPlaceholderSelection && selected.every((item) => !item.isPlaceholder) && selectedCropTypes.size === 1;
  if (!isPlaceholderSelection && !isSingleRealCropTypeSelection) return false;

  const totalRowsOfSelectedType = selectableRowKeys.filter((key) => {
    const item = getMissingItemById(key);
    if (!item) return false;
    return isPlaceholderSelection ? item.isPlaceholder : !item.isPlaceholder && selectedCropTypes.has(item.crop?.cropType);
  }).length;

  return selectedRowCount === totalRowsOfSelectedType;
};

export type CopyToPlaceholdersData = {
  templateCrop: BulkEditCrop;
  targetCrops: BulkEditCrop[];
  tillages: PracticeFCPO[];
  fertilizers: PracticeFCPO[];
};

// builds the BulkEditCropDrawer inputs for "copy 1 real crop's data onto N placeholders": templateCrop feeds the
// form's prefill only, targetCrops (fresh ids, so the save creates new crops rather than updating the source) are
// the only crops actually written
export const buildCopyToPlaceholdersData = (
  sourceItem: MissingDataForm | undefined,
  placeholderItems: MissingDataForm[],
  allFieldsPractices: Record<string, FieldPracticeResponse> | undefined
): CopyToPlaceholdersData | null => {
  const sourceCrop = sourceItem?.crop;
  if (!sourceItem || !sourceCrop) return null;

  const sourceFertilizerManagement = allFieldsPractices?.[sourceItem.fieldId]?.fertilizerManagements?.find(
    (fertilizerManagement) => fertilizerManagement.fieldCropId === sourceCrop.id && fertilizerManagement.year === sourceItem.year
  );

  const templateCrop: BulkEditCrop = {
    ...sourceCrop,
    fieldId: sourceItem.fieldId,
    year: sourceItem.year,
    isMandatory: false,
    isBaseline: false,
    isPlaceholder: false,
    availablePracticeBundles: allFieldsPractices?.[sourceItem.fieldId]?.availablePracticeBundles ?? [],
    fertilizerManagement: sourceFertilizerManagement ? [sourceFertilizerManagement] : [],
  };

  const targetCrops: BulkEditCrop[] = placeholderItems.map((placeholderItem) => {
    const id = generateRandomId();
    const fertilizerManagement: FertilizerManagement[] = sourceFertilizerManagement
      ? [{ ...sourceFertilizerManagement, year: placeholderItem.year, fieldCropId: id }]
      : [];

    return {
      ...sourceCrop,
      id,
      fieldId: placeholderItem.fieldId,
      year: placeholderItem.year,
      isMandatory: false,
      isBaseline: false,
      isPlaceholder: true,
      availablePracticeBundles: allFieldsPractices?.[placeholderItem.fieldId]?.availablePracticeBundles ?? [],
      fertilizerManagement,
    };
  });

  return {
    templateCrop,
    targetCrops,
    tillages: sourceItem.tillages ?? [],
    fertilizers: [...(sourceItem.organicFertilizers ?? []), ...(sourceItem.mineralFertilizers ?? [])],
  };
};
